import { useCallback } from 'react';
import type { BaseRecord } from '@refinedev/core';
import { useCustomMutation, useInvalidate, useList } from '@refinedev/core';
import { ApiError } from '@/api/errors';
import { notify } from '@/lib/notify';

/**
 * The master-data screens all do the same four things, so they share this.
 *
 * `pagination: { mode: 'off' }` is correct here and not laziness: these
 * collections are closed sets of a few dozen rows that the API returns as a
 * bare array with no cursor (see `master-data-admin.controller.ts`). Paging a
 * picker the wizard builder needs whole would be worse than the convention it
 * keeps.
 */
export interface AdminResourceResult<T> {
  rows: T[] | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  invalidate: () => void;
}

export function useAdminList<T>(resource: string): AdminResourceResult<T> {
  const invalidate = useInvalidate();
  const query = useList<BaseRecord>({
    resource,
    pagination: { mode: 'off' },
    queryOptions: { retry: 0 },
  });

  return {
    rows: query.query.isSuccess ? (query.result.data as T[]) : undefined,
    isLoading: query.query.isPending,
    error: query.query.error,
    refetch: () => {
      void query.query.refetch();
    },
    invalidate: () => {
      void invalidate({ resource, invalidates: ['list'] });
    },
  };
}

/**
 * Reorder is `PUT .../reorder` with the whole id list — not a Refine `update`,
 * because it addresses no single record.
 */
export function useReorder(resource: string, path: string): (ids: string[]) => Promise<void> {
  const { mutateAsync } = useCustomMutation();
  const invalidate = useInvalidate();

  return useCallback(
    async (ids: string[]) => {
      try {
        await mutateAsync({
          url: path,
          method: 'put',
          values: { ids },
          successNotification: false,
          errorNotification: false,
        });
        notify.success('Order saved');
      } catch (error) {
        notify.error(error, 'The new order was not saved');
      } finally {
        // Always resync: on success to pick up server-assigned positions, on
        // failure to put the row back where it actually is.
        void invalidate({ resource, invalidates: ['list'] });
      }
    },
    [mutateAsync, invalidate, resource, path],
  );
}

/** True when the API refused a delete because the row is still referenced. */
export function isInUse(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'OPTION_IN_USE';
}
